/**
 * SOCIOS Content section:
 * New account creation form.
 */

import { dId, logger } from "./common.js";
import { ajax } from "./ajax.js";

/**
 * User password validation:
 */

declare const PAGE_LANG: string;
const container_target = dId("user_form_container") as HTMLTemplateElement;

const login_pass = dId("password") as HTMLElement;
const password_view = dId("show_pass") as HTMLElement;

document.addEventListener('DOMContentLoaded', function () {
  prep_form();

  const form = document.getElementById("registro_form") as HTMLFormElement | null;
  if (form) {
    form.addEventListener("submit", handle_form_submit);
  }

  if (password_view && login_pass) {
    password_view.addEventListener("click", function () {
      if (login_pass.getAttribute("type") == "password") {
        login_pass.setAttribute("type", "text");
        password_view.style.opacity = "0.2";
        setTimeout(() => {
          login_pass.setAttribute("type", "password");
          password_view.style.opacity = "0.5";
        }, 2000);
      }
    });
  }
});

function show_form_message(message: string, isError = true) {
  const messageTarget = document.getElementById("form_error_message");
  if (!messageTarget) {
    logger(message);
    return;
  }

  messageTarget.textContent = message;
  messageTarget.classList.remove("status-error", "status-success");

  if (message) {
    messageTarget.classList.add(isError ? "status-error" : "status-success");
  }
}

function show_form_error(message: string) {
  show_form_message(message, true);
}

function show_form_success(message: string) {
  show_form_message(message, false);
}

function handle_form_submit(event: Event) {
  event.preventDefault();

  const form = event.currentTarget as HTMLFormElement | null;
  if (!form) {
    return;
  }

  const nameInput = form.querySelector("#nombre_completo") as HTMLInputElement | null;
  const emailInput = form.querySelector("#email") as HTMLInputElement | null;
  const dniInput = form.querySelector("#dni_nif") as HTMLInputElement | null;
  const phoneInput = form.querySelector("#phone") as HTMLInputElement | null;
  const citySelect = form.querySelector("#city") as HTMLSelectElement | null;
  const edadSelect = form.querySelector("#edad") as HTMLSelectElement | null;
  const passwordInput = form.querySelector("#password") as HTMLInputElement | null;

  const nameRegex = /^[A-Za-zÀ-ÿ'\-\s]{2,}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const dniRegex = /^[A-Z]?\d{7,8}[A-Z]$/;
  const phoneRegex = /^\+?[0-9\s-]{9,}$/;
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

  if (!nameInput || !nameRegex.test(nameInput.value.trim())) {
    show_form_error("Introduce un nombre válido.");
    return;
  }

  if (!emailInput || !emailRegex.test(emailInput.value.trim())) {
    show_form_error("Introduce un correo electrónico válido.");
    return;
  }

  if (!dniInput) {
    show_form_error("Introduce un DNI válido.");
    return;
  }

  const normalizedDni = dniInput.value.trim().replace(/-/g, "").toUpperCase();
  dniInput.value = normalizedDni;

  if (!dniRegex.test(normalizedDni)) {
    show_form_error("Introduce un DNI válido.");
    return;
  }

  if (!phoneInput || !phoneRegex.test(phoneInput.value.trim())) {
    show_form_error("Introduce un teléfono válido.");
    return;
  }

  if (!citySelect || !citySelect.value) {
    show_form_error("Selecciona una provincia.");
    return;
  }

  if (!edadSelect || !edadSelect.value) {
    show_form_error("Selecciona tu rango de edad.");
    return;
  }

  if (!passwordInput || !passwordRegex.test(passwordInput.value)) {
    show_form_error("La contraseña debe tener 8 caracteres, mayúsculas, minúsculas, un número y un símbolo.");
    return;
  }

  show_form_message("");
  create_user(form);
}

async function create_user(form: HTMLFormElement) {
  try {
    const formData = new FormData(form);
    const params = new URLSearchParams();

    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    const response = await fetch(ajax.get("newuser"), {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: params
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || data.error || "Unknown error");
    }

    show_form_success(data.message || "Cuenta creada correctamente.");
    form.reset();
    prep_form();
  } catch (error) {
    const message = error instanceof Error
      ? error.message
      : "An error occurred while creating user.";

    logger("Error creating user: " + message);
    show_form_error(message);
  }
}

function render_error(message: string) {
  if (!container_target) {
    logger(message);
    return;
  }

  container_target.innerHTML = `<div class="error-message">${message}</div>`;
}

function prep_form() {
  // Provincias data
  const provincias = {
    0: 'A Coruña',
    1: 'Álava',
    2: 'Albacete',
    3: 'Alicante',
    4: 'Almería',
    5: 'Asturias',
    6: 'Ávila',
    7: 'Badajoz',
    8: 'Barcelona',
    9: 'Burgos',
    10: 'Cáceres',
    11: 'Cádiz',
    12: 'Cantabria',
    13: 'Castellón',
    15: 'Ciudad Real',
    16: 'Córdoba',
    17: 'Cuenca',
    18: 'Girona',
    19: 'Granada',
    20: 'Guadalajara',
    21: 'Guipúzcoa',
    22: 'Huelva',
    24: 'Illes Balears',
    25: 'Jaén',
    26: 'La Rioja',
    27: 'Las Palmas',
    28: 'León',
    29: 'Lleida',
    30: 'Lugo',
    31: 'Madrid',
    32: 'Málaga',
    34: 'Murcia',
    35: 'Navarra',
    36: 'Ourense',
    37: 'Palencia',
    38: 'Pontevedra',
    39: 'Salamanca',
    40: 'Santa Cruz de Tenerife',
    41: 'Segovia',
    42: 'Sevilla',
    43: 'Soria',
    44: 'Tarragona',
    46: 'Toledo',
    47: 'Valencia',
    48: 'Valladolid',
    49: 'Vizcaya',
    50: 'Zamora',
    51: 'Zaragoza'
  };

  // Age groups data
  const edadRangos = {
    1: '18-25 años',
    2: '26-35 años',
    3: '36-50 años',
    4: '51-70 años',
    5: '70+ años'
  };

  const provinciaSelect = document.getElementById('city') as HTMLSelectElement | null;

  if (provinciaSelect && provinciaSelect.options.length === 0) {
    const defaultProvinciaOption = document.createElement('option');
    defaultProvinciaOption.value = '';
    defaultProvinciaOption.textContent = 'Seleccione una provincia';
    provinciaSelect.appendChild(defaultProvinciaOption);

    for (const [id, nombre] of Object.entries(provincias)) {
      const option = document.createElement('option');
      option.value = String(id);
      option.textContent = nombre;
      provinciaSelect.appendChild(option);
    }
  }

  const edadSelect = document.getElementById('edad') as HTMLSelectElement | null;
  if (edadSelect && edadSelect.options.length === 0) {
    const defaultEdadOption = document.createElement('option');
    defaultEdadOption.value = '';
    defaultEdadOption.textContent = 'Seleccione su rango de edad';
    edadSelect.appendChild(defaultEdadOption);

    for (const [id, rango] of Object.entries(edadRangos)) {
      const option = document.createElement('option');
      option.value = String(id);
      option.textContent = rango;
      edadSelect.appendChild(option);
    }
  }
}
